import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "../../../environments/environment";
import { forkJoin, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";

import _ from "lodash";

declare let $: any;

@Injectable({
  providedIn: "root",
})
export class FriendService {
  constructor(public http: HttpClient) {}

  private normalizeUser(user: any): any {
    if (!user || typeof user !== "object") {
      return null;
    }
    return {
      ...user,
      fullName: user.fullName || user.fullname || "",
      fullname: user.fullname || user.fullName || "",
      profileImage: user.profileImage || user.thumbnail || "",
      thumbnail: user.thumbnail || user.profileImage || "",
    };
  }

  list(inParams: any = {$populate: ['user', 'friend']}): any {
    const params = {...inParams};

    if (params.$page) {
      params.$skip = (params.$page - 1) * (params.$limit);
    }
    delete params.$page;

    if (!params.status) {
      delete params.status;
    }

    const query = $.param(params);
    const url = `${environment.taskSharingApiUrl}/friends?${query}`;
    return this.http.get(url).pipe(
      switchMap((resp: any) => {
        const rows = Array.isArray(resp?.data) ? resp.data : [];
        if (!rows.length) {
          return of(resp);
        }

        const missingRows = rows.filter(
          (row: any) => !row.friendDetails || !row.userDetails || !row.authorDetails
        );
        if (!missingRows.length) {
          return of(resp);
        }

        const idSet = new Set<number>();
        missingRows.forEach((row: any) => {
          [row.user, row.friend, row.author].forEach((id) => {
            const num = Number(id);
            if (Number.isFinite(num)) {
              idSet.add(num);
            }
          });
        });

        const ids = Array.from(idSet);
        if (!ids.length) {
          return of(resp);
        }

        const requests = ids.map((id) =>
          this.http.get(`${environment.accountApiUrl}/users/${id}`).pipe(
            map((userResp: any) => [id, this.normalizeUser(userResp?.data || userResp)] as const)
          )
        );

        return forkJoin(requests).pipe(
          map((pairs) => {
            const userMap = new Map<number, any>(pairs);
            const patchedData = rows.map((row: any) => {
              const userId = Number(row.user);
              const friendId = Number(row.friend);
              const authorId = Number(row.author);
              return {
                ...row,
                userDetails: row.userDetails || userMap.get(userId) || null,
                friendDetails: row.friendDetails || userMap.get(friendId) || null,
                authorDetails: row.authorDetails || userMap.get(authorId) || null,
              };
            });
            return { ...resp, data: patchedData };
          })
        );
      })
    );
  }

  save(model, params: any = null): any {
    const query = params ? $.param(params) : "";
    const querySuffix = query ? `?${query}` : "";
    if (model.id) {
      const updateUrl = `${environment.taskSharingApiUrl}/friends/${model.id}${querySuffix}`;
      return this.http.patch(updateUrl, model);
    }

    delete model.id;
    const url = `${environment.taskSharingApiUrl}/friends${querySuffix}`;
    return this.http.post(url, model);
  }

  detail(id): any {
    const url = `${environment.taskSharingApiUrl}/friends/${id}`;
    const params: any = {
      $populate: ["user", "author"],
    };

    return this.http.get(url, { params });
  }

  delete(model): any {
    const url = `${environment.taskSharingApiUrl}/friends/${model.id}`;
    return this.http.delete(url);
  }
}
